import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api, { authApi } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('asm_user');
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(false);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const res = await authApi.login(email, password);
      const { access_token, refresh_token, user: userData } = res.data;
      localStorage.setItem('asm_token', access_token);
      localStorage.setItem('asm_refresh', refresh_token);
      localStorage.setItem('asm_user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    // Send refresh token to server so it can be blacklisted too
    const refreshToken = localStorage.getItem('asm_refresh');
    try {
      await api.post('/auth/logout', { refresh_token: refreshToken });
    } catch (e) {
      // Logout should proceed even if the API call fails
    }
    localStorage.removeItem('asm_token');
    localStorage.removeItem('asm_refresh');
    localStorage.removeItem('asm_user');
    setUser(null);
  }, []);

  const updateUser = useCallback((updatedUser) => {
    localStorage.setItem('asm_user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  }, []);

  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    const perms = {
      owner: ['*'],
      security_admin: ['view_devices','view_employees','view_events','view_screenshots','view_recordings','view_alerts','manage_alerts','start_remote','view_audit','view_analytics','export_data','view_users'],
      it_admin: ['view_devices','view_employees','manage_devices','start_remote','view_alerts','manage_alerts'],
      manager: ['view_employees','view_analytics','view_alerts'],
      auditor: ['view_audit','view_alerts','export_data'],
    };
    const rolePerms = perms[user.role] || [];
    return rolePerms.includes('*') || rolePerms.includes(permission);
  }, [user]);

  useEffect(() => {
    if (user && localStorage.getItem('asm_token')) {
      authApi.me().then(res => {
        // Sync fresh server state (force_password_change, etc.)
        const fresh = { ...user, ...res.data };
        localStorage.setItem('asm_user', JSON.stringify(fresh));
        setUser(fresh);
      }).catch(() => logout());
    }
  }, []); // eslint-disable-line

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, hasPermission, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

